/**
 * IHODashboard — Panel del Internal Health Officer
 * Muestra las alertas clínicas de todos los empleados de la empresa del IHO.
 */
import { useState, useEffect, useCallback } from "react";
import { collection, getDocs, query, where, doc, updateDoc } from "firebase/firestore";
import { db } from "@/firebase";
import { useUserProfile } from "@/contexts/UserProfileContext";
import {
  ShieldAlert, RefreshCw, Loader2, CheckCircle2, AlertTriangle,
  AlertCircle, Clock, User, Building2, ChevronDown, ChevronUp,
} from "lucide-react";

const SEVERITY_CONFIG = {
  critical: { label: "Crítico", bg: "#FEF2F2", border: "#FCA5A5", color: "#B91C1C", dot: "#DC2626", icon: AlertCircle },
  danger:   { label: "Peligro", bg: "#FFF7ED", border: "#FED7AA", color: "#C2410C", dot: "#F97316", icon: AlertTriangle },
  warning:  { label: "Atención", bg: "#FEFCE8", border: "#FEF08A", color: "#854D0E", dot: "#EAB308", icon: AlertTriangle },
};

function SeverityBadge({ severity }) {
  const cfg = SEVERITY_CONFIG[severity] || SEVERITY_CONFIG.warning;
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold"
      style={{ background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}` }}
    >
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: cfg.dot, display: "inline-block" }} />
      {cfg.label}
    </span>
  );
}

function fmtDateTime(raw) {
  if (!raw) return "—";
  let date;
  if (raw?.toDate) date = raw.toDate();
  else if (raw?.seconds) date = new Date(raw.seconds * 1000);
  else if (typeof raw === "string") date = new Date(raw);
  else return "—";
  return date.toLocaleString("es-CO", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function EmployeeCard({ employee, alerts, onMarkSeen }) {
  const [expanded, setExpanded] = useState(false);
  const unseenAlerts = alerts.filter((a) => !a.seen);
  const criticalCount = alerts.filter((a) => a.severity === "critical").length;
  const dangerCount = alerts.filter((a) => a.severity === "danger").length;

  const name = employee.displayName ||
    `${employee.profile?.nombres || employee.profile?.name || ""} ${employee.profile?.apellidos || ""}`.trim() ||
    employee.email || "—";

  const hasCritical = criticalCount > 0;
  const cardBorder = hasCritical ? "#FCA5A5" : dangerCount > 0 ? "#FED7AA" : "#E2E8F0";

  return (
    <div
      className="rounded-xl overflow-hidden"
      style={{ background: "white", border: `1px solid ${cardBorder}` }}
      data-testid={`iho-employee-card-${employee.uid}`}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between px-4 py-3 cursor-pointer"
        style={{ background: hasCritical ? "#FFF5F5" : "#F8FAFC" }}
        onClick={() => setExpanded((p) => !p)}
      >
        <div className="flex items-center gap-3">
          <div
            className="w-9 h-9 rounded-full flex items-center justify-center shrink-0"
            style={{ background: hasCritical ? "#FEE2E2" : "#E0F2FE" }}
          >
            <User size={16} style={{ color: hasCritical ? "#B91C1C" : "#0284C7" }} />
          </div>
          <div>
            <p className="text-sm font-semibold" style={{ color: "#1E293B" }}>{name}</p>
            <p className="text-[11px]" style={{ color: "#64748B" }}>{employee.email || "—"}</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {criticalCount > 0 && (
            <span className="text-[11px] font-bold px-2 py-0.5 rounded-full" style={{ background: "#FEE2E2", color: "#B91C1C" }}>
              {criticalCount} crítico{criticalCount > 1 ? "s" : ""}
            </span>
          )}
          {dangerCount > 0 && (
            <span className="text-[11px] font-bold px-2 py-0.5 rounded-full" style={{ background: "#FFF7ED", color: "#C2410C" }}>
              {dangerCount} peligro
            </span>
          )}
          {unseenAlerts.length > 0 && (
            <span
              className="text-[10px] font-bold px-1.5 py-0.5 rounded-full"
              style={{ background: "#DBEAFE", color: "#1D4ED8" }}
            >
              {unseenAlerts.length} nueva{unseenAlerts.length > 1 ? "s" : ""}
            </span>
          )}
          {alerts.length === 0 && (
            <span className="text-[11px]" style={{ color: "#16A34A" }}>Sin alertas activas</span>
          )}
          {expanded ? <ChevronUp size={14} style={{ color: "#94A3B8" }} /> : <ChevronDown size={14} style={{ color: "#94A3B8" }} />}
        </div>
      </div>

      {/* Alert list */}
      {expanded && alerts.length > 0 && (
        <div className="border-t" style={{ borderColor: "#F1F5F9" }}>
          {alerts.map((a) => {
            const cfg = SEVERITY_CONFIG[a.severity] || SEVERITY_CONFIG.warning;
            const Icon = cfg.icon;
            return (
              <div
                key={a.id}
                className="flex items-start justify-between px-4 py-3 border-b"
                style={{
                  borderColor: "#F8FAFC",
                  background: !a.seen ? cfg.bg : "white",
                  opacity: a.seen ? 0.65 : 1,
                }}
                data-testid={`iho-alert-${a.id}`}
              >
                <div className="flex items-start gap-2.5 flex-1">
                  <Icon size={14} className="mt-0.5 shrink-0" style={{ color: cfg.color }} />
                  <div>
                    <p className="text-sm font-medium" style={{ color: "#1E293B" }}>{a.text}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <SeverityBadge severity={a.severity} />
                      <span className="text-[10px]" style={{ color: "#94A3B8" }}>
                        <Clock size={9} className="inline mr-0.5" />
                        {fmtDateTime(a.triggeredAt)}
                      </span>
                    </div>
                  </div>
                </div>
                {!a.seen && (
                  <button
                    onClick={() => onMarkSeen(employee.uid, a.id)}
                    className="shrink-0 ml-3 flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-semibold cursor-pointer transition-all hover:opacity-80"
                    style={{ background: "#F0FDF4", color: "#16A34A", border: "1px solid #BBF7D0" }}
                    data-testid={`mark-seen-${a.id}`}
                  >
                    <CheckCircle2 size={11} />
                    Revisado
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {expanded && alerts.length === 0 && (
        <div className="px-4 py-4 text-sm text-center border-t" style={{ color: "#94A3B8", borderColor: "#F1F5F9" }}>
          Sin alertas recientes para este empleado.
        </div>
      )}
    </div>
  );
}

export default function IHODashboard() {
  const { clientId, isIHO } = useUserProfile();
  const [employees, setEmployees] = useState([]);
  const [alertsByUid, setAlertsByUid] = useState({});
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [filter, setFilter] = useState("all"); // all | critical | unseen

  const loadData = useCallback(async () => {
    if (!clientId) { setLoading(false); return; }
    setLoading(true);

    // Load all employees from the same company
    const empSnap = await getDocs(query(collection(db, "users"), where("clientId", "==", clientId)));
    const empList = empSnap.docs.map((d) => ({ uid: d.id, ...d.data() }));
    setEmployees(empList);

    // Load alerts for all employees (last 30 days)
    const cutoff = Date.now() - 30 * 24 * 3600 * 1000;
    const alertMap = {};
    await Promise.all(empList.map(async (emp) => {
      try {
        const aSnap = await getDocs(collection(db, "users", emp.uid, "alerts"));
        const now = Date.now();
        const empAlerts = aSnap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((a) => {
            if (!["critical", "danger"].includes(a.severity)) return false;
            if (a.resolved) return false;
            const expiresMs = a.expiresAt?.toMillis?.() || (a.expiresAt?.seconds ? a.expiresAt.seconds * 1000 : now + 1);
            if (expiresMs < now) return false;
            return true;
          })
          .sort((a, b) => {
            const msA = a.triggeredAt?.toMillis?.() || (a.triggeredAt?.seconds ? a.triggeredAt.seconds * 1000 : 0);
            const msB = b.triggeredAt?.toMillis?.() || (b.triggeredAt?.seconds ? b.triggeredAt.seconds * 1000 : 0);
            return msB - msA;
          });
        alertMap[emp.uid] = empAlerts;
      } catch (_) {
        alertMap[emp.uid] = [];
      }
    }));
    setAlertsByUid(alertMap);
    setLastRefresh(new Date());
    setLoading(false);
  }, [clientId]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleMarkSeen = useCallback((empUid, alertId) => {
    // Actualización optimista — cambio de estado síncrono antes del write a Firestore
    setAlertsByUid((prev) => ({
      ...prev,
      [empUid]: (prev[empUid] || []).map((a) =>
        a.id === alertId ? { ...a, seen: true } : a
      ),
    }));
    // Fire-and-forget — sin setState después del await
    updateDoc(doc(db, "users", empUid, "alerts", alertId), { seen: true }).catch(() => {});
  }, []);

  // Stats
  const totalEmployees = employees.length;
  const employeesWithCritical = employees.filter((e) => (alertsByUid[e.uid] || []).some((a) => a.severity === "critical")).length;
  const totalUnseen = Object.values(alertsByUid).flat().filter((a) => !a.seen).length;
  const totalAlerts = Object.values(alertsByUid).flat().length;

  // Filter employees
  const filteredEmployees = employees.filter((e) => {
    const empAlerts = alertsByUid[e.uid] || [];
    if (filter === "critical") return empAlerts.some((a) => a.severity === "critical");
    if (filter === "unseen") return empAlerts.some((a) => !a.seen);
    return true;
  });

  // Sort: critical first, then danger, then by unseen count
  const sortedEmployees = [...filteredEmployees].sort((a, b) => {
    const aAlerts = alertsByUid[a.uid] || [];
    const bAlerts = alertsByUid[b.uid] || [];
    const aCrit = aAlerts.filter((x) => x.severity === "critical").length;
    const bCrit = bAlerts.filter((x) => x.severity === "critical").length;
    if (bCrit !== aCrit) return bCrit - aCrit;
    const aUnseen = aAlerts.filter((x) => !x.seen).length;
    const bUnseen = bAlerts.filter((x) => !x.seen).length;
    return bUnseen - aUnseen;
  });

  if (!isIHO) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#F1F5F9" }}>
        <div className="text-center p-8 bg-white rounded-2xl shadow-sm max-w-sm">
          <ShieldAlert size={40} className="mx-auto mb-4" style={{ color: "#94A3B8" }} />
          <h2 className="text-lg font-bold mb-2" style={{ color: "#1E293B" }}>Acceso restringido</h2>
          <p className="text-sm" style={{ color: "#64748B" }}>
            Esta sección es exclusiva para oficiales de salud (IHO) designados.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: "#F1F5F9" }}>
      {/* Header */}
      <div className="px-6 py-5 border-b" style={{ background: "white", borderColor: "#E2E8F0" }}>
        <div className="max-w-4xl mx-auto flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "#DBEAFE" }}>
              <ShieldAlert size={20} style={{ color: "#1D4ED8" }} />
            </div>
            <div>
              <h1 className="text-lg font-bold" style={{ color: "#1E293B" }}>Monitor IHO</h1>
              <p className="text-xs" style={{ color: "#64748B" }}>
                <Building2 size={10} className="inline mr-1" />
                Panel de salud empresarial — últimos 30 días
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={loadData}
              disabled={loading}
              className="p-2 rounded-lg border cursor-pointer transition-colors hover:bg-gray-50"
              style={{ borderColor: "#E2E8F0" }}
              data-testid="iho-refresh-btn"
            >
              <RefreshCw size={15} style={{ color: "#64748B" }} className={loading ? "animate-spin" : ""} />
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-6 space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: "Empleados", value: totalEmployees, bg: "#E0F2FE", color: "#0284C7", testid: "iho-stat-employees" },
            { label: "Con alertas críticas", value: employeesWithCritical, bg: "#FEE2E2", color: "#B91C1C", testid: "iho-stat-critical" },
            { label: "Alertas totales", value: totalAlerts, bg: "#FFF7ED", color: "#C2410C", testid: "iho-stat-total" },
            { label: "Sin revisar", value: totalUnseen, bg: "#DBEAFE", color: "#1D4ED8", testid: "iho-stat-unseen" },
          ].map((s) => (
            <div key={s.label} className="bg-white rounded-xl p-4 border border-gray-100" data-testid={s.testid}>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: s.bg }}>
                  <span className="text-sm font-bold" style={{ color: s.color }}>{s.value}</span>
                </div>
                <p className="text-xs" style={{ color: "#64748B" }}>{s.label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Filter tabs */}
        <div className="flex items-center gap-2">
          {[
            { id: "all", label: `Todos (${employees.length})` },
            { id: "critical", label: `Críticos (${employeesWithCritical})` },
            { id: "unseen", label: `Sin revisar (${totalUnseen})` },
          ].map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold cursor-pointer transition-all"
              style={{
                background: filter === f.id ? "#1D4ED8" : "white",
                color: filter === f.id ? "white" : "#64748B",
                border: `1px solid ${filter === f.id ? "#1D4ED8" : "#E2E8F0"}`,
              }}
              data-testid={`filter-${f.id}`}
            >
              {f.label}
            </button>
          ))}
          {lastRefresh && (
            <span className="ml-auto text-[10px]" style={{ color: "#94A3B8" }}>
              Actualizado: {lastRefresh.toLocaleTimeString("es-CO", { hour: "2-digit", minute: "2-digit" })}
            </span>
          )}
        </div>

        {/* Employee cards */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 size={24} className="animate-spin" style={{ color: "#1D4ED8" }} />
            <span className="text-sm ml-2" style={{ color: "#64748B" }}>Cargando datos de empleados...</span>
          </div>
        ) : !clientId ? (
          <div className="text-center py-12 text-sm" style={{ color: "#94A3B8" }}>
            Tu cuenta no está asociada a ninguna empresa. Contacta al administrador.
          </div>
        ) : sortedEmployees.length === 0 ? (
          <div className="text-center py-12">
            <CheckCircle2 size={32} className="mx-auto mb-3" style={{ color: "#16A34A" }} />
            <p className="text-sm font-semibold" style={{ color: "#1E293B" }}>
              {filter !== "all" ? "Sin empleados con ese filtro" : "Sin alertas en los últimos 30 días"}
            </p>
            <p className="text-xs mt-1" style={{ color: "#64748B" }}>
              {filter !== "all" ? `Mostrando filtro: ${filter}` : "Todos los empleados tienen valores dentro de los rangos normales."}
            </p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="iho-employee-list">
            {sortedEmployees.map((emp) => (
              <EmployeeCard
                key={emp.uid}
                employee={emp}
                alerts={alertsByUid[emp.uid] || []}
                onMarkSeen={handleMarkSeen}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
